import {applyEmbedTheme, type FwThemeOption, resolveTheme} from '@/tenant/utils/theme';
import vuetify from '@/plugins/vuetify';

/**
 * Customer-facing SPA path prefixes (mirror of SpaPathAllowlist::STOREFRONT_PREFIXES).
 * Store themes must only ever restyle these — an admin previewing their store in a
 * dashboard session must not carry the theme back into the dashboard.
 */
const STOREFRONT_PREFIXES = ['rental', 'public', 'invoice', 'pay', 'signing'];

export function isStorefrontPath(pathname: string): boolean {
    const firstSegment = pathname.split('/').filter(Boolean)[0] ?? '';
    return STOREFRONT_PREFIXES.includes(firstSegment);
}

const isHex = (value: string | undefined): value is string =>
    !!value && /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value);

/** Parses a hex or rgb()/rgba() color into [r, g, b] (0–255), or null. */
function parseColorChannels(value: string | undefined): [number, number, number] | null {
    if (!value) {
        return null;
    }

    if (isHex(value)) {
        let hex = value.slice(1);
        if (hex.length === 3) {
            hex = hex.split('').map((c) => c + c).join('');
        }
        return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
    }

    const rgbMatch = value.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
    if (rgbMatch) {
        return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
    }

    return null;
}

/**
 * Whether a theme's card surface is dark. Storefront components author their
 * dark styling with Tailwind `dark:` variants (darkMode: 'class'), so a dark
 * store theme must activate that class — otherwise near-black utility text
 * lands on the theme's near-black surfaces. Unparseable/missing surfaces are
 * treated as light (the built-in fallback look). Pure — exported for tests.
 */
export function isDarkSurfaceTheme(theme: FwThemeOption | null | undefined): boolean {
    const resolved = resolveTheme(theme);
    const channels = parseColorChannels(resolved['--fw-surface'] ?? resolved['--fw-surface-alt']);

    if (!channels) {
        return false;
    }

    const [r, g, b] = channels;
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance < 0.5;
}

/**
 * Sets the store font inline on the Vuetify `.v-application` root — the only
 * declaration that beats Vuetify's own layered !important base font, so all
 * descendants inherit it. The theme can apply before VApp mounts, and VApp may
 * re-mount during initial load, so re-apply on a short interval (~2s) to land
 * on the settled root regardless of timing.
 */
function applyRootFont(font: string): void {
    let tries = 0;
    const tick = () => {
        const appRoot = document.querySelector('.v-application') as HTMLElement | null;
        appRoot?.style.setProperty('font-family', font, 'important');
        if (++tries < 20) {
            window.setTimeout(tick, 100);
        }
    };
    tick();
}

/**
 * Maps validated theme tokens onto Vuetify runtime theme colors so the whole
 * store (which renders on Vuetify surfaces) follows the theme — not just the
 * bespoke components that read --fw-* directly. Vuetify only reliably parses
 * hex, so non-hex tokens still apply via the --fw-* variables. Pure — exported
 * for tests.
 */
export function mapThemeToVuetifyColors(theme: FwThemeOption): Record<string, string> {
    const resolved = resolveTheme(theme);
    const colors: Record<string, string> = {};

    const map: Record<string, string> = {
        'primary': '--fw-accent',
        'on-primary': '--fw-accent-contrast',
        'surface': '--fw-surface',
        'surface-bright': '--fw-surface-alt',
        'surface-light': '--fw-surface-alt',
        // Page chrome matches the editor preview: surfaceAlt as the canvas, surface for cards.
        'background': '--fw-surface-alt',
        'on-surface': '--fw-text',
        'on-background': '--fw-text',
    };

    for (const [colorKey, cssVar] of Object.entries(map)) {
        if (isHex(resolved[cssVar])) {
            colors[colorKey] = resolved[cssVar];
        }
    }

    return colors;
}

/**
 * Paints the hosted storefront page canvas (html/body/main) to match the theme
 * preview — surfaceAlt as the page background. Embeds must never do this; they
 * only set --fw-* tokens and leave the host page alone.
 */
export function storePageChromeCss(): string {
    return [
        'html, body {',
        '  background-color: var(--fw-surface-alt, var(--fw-surface, #f7fafa)) !important;',
        '  color: var(--fw-text, #213b47);',
        '}',
        '#main-tenant-content {',
        '  background-color: var(--fw-surface-alt, var(--fw-surface, #f7fafa)) !important;',
        '  color: var(--fw-text, #213b47);',
        '}',
        '.fw-store-nav, .fw-store-footer {',
        '  background-color: var(--fw-surface, #ffffff) !important;',
        '  border-color: var(--fw-border, #dfe5e8) !important;',
        '  color: var(--fw-text, #213b47);',
        '}',
        '.fw-store-footer {',
        '  color: var(--fw-muted, #7b8b93);',
        '}',
        // V-Calendar: blend the panel into the card (like the phone field) and
        // recolor the DEFAULT (vc-blue) selection ramp from the store accent.
        // Attribute-level colors (.vc-red/.vc-orange availability markers)
        // re-scope the ramp on their own elements, so they keep their meaning.
        '.vc-container {',
        '  background: transparent;',
        '}',
        // The blue class is on the container AND re-applied per selection
        // attribute element, so both need the recolor; red/orange attribute
        // elements never match and keep their palettes.
        '.vc-container.vc-blue,',
        '.vc-container .vc-attr.vc-blue {',
        '  --vc-accent-50: color-mix(in srgb, var(--fw-accent, #3b82f6) 8%, white);',
        '  --vc-accent-100: color-mix(in srgb, var(--fw-accent, #3b82f6) 16%, white);',
        '  --vc-accent-200: color-mix(in srgb, var(--fw-accent, #3b82f6) 30%, white);',
        '  --vc-accent-300: color-mix(in srgb, var(--fw-accent, #3b82f6) 45%, white);',
        '  --vc-accent-400: color-mix(in srgb, var(--fw-accent, #3b82f6) 70%, white);',
        '  --vc-accent-500: var(--fw-accent, #3b82f6);',
        '  --vc-accent-600: color-mix(in srgb, var(--fw-accent, #3b82f6) 90%, black);',
        '  --vc-accent-700: color-mix(in srgb, var(--fw-accent, #3b82f6) 75%, black);',
        '  --vc-accent-800: color-mix(in srgb, var(--fw-accent, #3b82f6) 60%, black);',
        '  --vc-accent-900: color-mix(in srgb, var(--fw-accent, #3b82f6) 45%, black);',
        '}',
        // The solid start/end discs take the accent-contrast text color so a
        // light accent (luxury gold) gets dark digits instead of white.
        '.vc-container .vc-attr.vc-blue {',
        '  --vc-highlight-solid-content-color: var(--fw-accent-contrast, #ffffff);',
        '}',
    ].join('\n');
}

function applyStorePageChrome(): void {
    let styleEl = document.getElementById('fw-store-chrome') as HTMLStyleElement | null;
    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'fw-store-chrome';
        document.head.appendChild(styleEl);
    }
    styleEl.textContent = storePageChromeCss();
}

/**
 * Applies a company's store theme to the hosted storefront: the shared --fw-*
 * variables (same engine as the embed), page chrome background (hosted only),
 * the chosen font (loaded + set on the app root so all text inherits it), and
 * Vuetify's runtime theme so framework components follow along.
 *
 * The server only includes `store_theme` in public company payloads when the
 * StoreThemes Pennant feature is active; presence of a theme is the gate, and
 * this additionally refuses to run outside storefront routes.
 */
export function applyStoreTheme(theme: FwThemeOption | null | undefined): void {
    if (!theme || !isStorefrontPath(window.location.pathname)) {
        return;
    }

    applyEmbedTheme(theme);
    applyStorePageChrome();

    // Storefront components author dark styling as Tailwind `dark:` variants;
    // a dark-surface theme must turn them on (and Vuetify's dark theme with
    // them) or utility text/backgrounds stay light-mode and vanish on dark surfaces.
    const isDark = isDarkSurfaceTheme(theme);
    document.documentElement.classList.toggle('dark', isDark);

    const resolved = resolveTheme(theme);

    const font = resolved['--fw-font'];
    if (font && font !== 'inherit') {
        // Two mechanisms, because no single one covers everything:
        //  - Stylesheet targets `body` (timing-safe) and headings directly — the
        //    brand's `h1,h2,h3 { Space Grotesk }` beats an inherited font, and
        //    importance doesn't pass through inheritance.
        //  - Inline on `.v-application`: Vuetify sets that root's font via a
        //    *layered* !important rule which beats our unlayered !important, so
        //    only an inline style wins — and descendants inherit from it.
        let styleEl = document.getElementById('fw-store-font') as HTMLStyleElement | null;
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'fw-store-font';
            document.head.appendChild(styleEl);
        }
        styleEl.textContent =
            `body, .v-application :where(h1,h2,h3,h4,h5,h6) { font-family: ${font} !important; }`;

        applyRootFont(font);
    }

    const colors = mapThemeToVuetifyColors(theme);

    try {
        const themes = (vuetify.theme.themes as { value?: Record<string, { colors?: Record<string, string> }> }).value;
        for (const name of ['light', 'dark']) {
            const target = themes?.[name];
            if (target?.colors && Object.keys(colors).length) {
                Object.assign(target.colors, colors);
            }
        }

        // Activate the matching Vuetify theme so derived colors (field
        // outlines, emphasis opacities, hover overlays) fit the surface too.
        const themeApi = vuetify.theme as unknown as {
            change?: (name: string) => void;
            global?: { name?: { value: string } };
        };
        const themeName = isDark ? 'dark' : 'light';
        if (typeof themeApi.change === 'function') {
            themeApi.change(themeName);
        } else if (themeApi.global?.name) {
            themeApi.global.name.value = themeName;
        }
    } catch {
        // Vuetify runtime theme unavailable — the CSS variables are still applied.
    }
}
